import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type CSSProperties } from 'react'
import type { EChartsType } from 'echarts/core'
import type { EChartsOption } from 'echarts'
import { cn } from '../../../lib/cn'
import { loadECharts } from './echartsLoader'
import { prefersTexture, useChartTheme } from './useChartTheme'
import { chartPngWithContext } from './png'

/** Параметры события клика/наведения ECharts — та часть, которой пользуются компоненты. */
export interface EChartEventParams {
  componentType?: string
  seriesType?: string
  seriesIndex?: number
  seriesId?: string
  seriesName?: string
  dataIndex?: number
  name?: string
  value?: unknown
  data?: any
  /** Для кликов по подписям осей (`triggerEvent: true`). */
  targetType?: string
}

export interface EChartHandle {
  /** Экземпляр ECharts (после ленивой загрузки), иначе `null`. */
  getInstance: () => EChartsType | null
  /** PNG графика как data URL (фон — поверхность текущей темы). */
  getDataURL: () => string | null
  /** Скачать PNG. */
  exportPng: (fileName: string, context?: string) => void | Promise<void>
}

export interface EChartHighlight {
  seriesIndex?: number
  dataIndex?: number
  name?: string
}

export interface EChartProps {
  option: EChartsOption
  /** Высота области графика; по умолчанию 320 px. Ширина — по контейнеру. */
  height?: number | string
  /** Идёт перезагрузка данных: держим прежний кадр с прозрачностью 0,5, без скелетона и прыжка вёрстки. */
  loading?: boolean
  renderer?: 'canvas' | 'svg'
  /** Подпись графика для скринридера. ECharts дополнительно описывает данные (aria). */
  ariaLabel?: string
  className?: string
  /** Текстура (decal) вместо одного цвета: для печати и дальтонизма. Принудительные цвета ОС включают её сами. */
  texture?: boolean
  /** Клик по метке: столбцу, точке, ячейке, сегменту, подписи оси. */
  onMarkClick?: (params: EChartEventParams) => void
  /** Наведение на метку (`null` — ушли с метки): связь графика с таблицей. */
  onMarkHover?: (params: EChartEventParams | null) => void
  /** Клик в любом месте области построения по категории оси X (номер категории). */
  onAxisClick?: (index: number, seriesId?: string) => void
  /** Выделение диапазона мышью по оси X: номера первой и последней категорий. */
  onBrushX?: (startIndex: number, endIndex: number) => void
  /** Подсветка метки извне (наведение на строку таблицы). */
  highlight?: EChartHighlight | null
  /** Вызывается один раз после инициализации движка. */
  onReady?: (chart: EChartsType) => void
}

/** Сдвиг мыши (px), после которого нажатие считается выделением диапазона, а не кликом. */
const BRUSH_THRESHOLD = 8

function withTexture(option: EChartsOption, texture: boolean): EChartsOption {
  if (!texture) return option
  const aria = (option.aria ?? {}) as Record<string, unknown>
  return { ...option, aria: { ...aria, enabled: true, decal: { show: true } } } as EChartsOption
}

/** Категория оси X под точкой `x` (px) или `null`, если вне области построения. */
function categoryAt(chart: EChartsType, x: number, y: number): number | null {
  try {
    if (!chart.containPixel({ gridIndex: 0 }, [x, y])) return null
    const value = chart.convertFromPixel({ xAxisIndex: 0 }, x) as unknown
    const index = Math.round(Array.isArray(value) ? Number(value[0]) : Number(value))
    return Number.isFinite(index) ? index : null
  } catch {
    return null
  }
}

function capturePng(chart: EChartsType, backgroundColor: string): string {
  // Options are normally applied lazily. Flush the model before exporting so a
  // new period's caption cannot be attached to the previous animation frame.
  chart.setOption({}, { lazyUpdate: false })
  const renderer = chart.getZr()
  renderer.storage.getDisplayList().forEach((element) => element.stopAnimation(undefined, true))
  renderer.refreshImmediately()
  return chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor })
}

/**
 * Базовая обёртка ECharts дизайн-системы: ленивая загрузка движка, подгон под ширину (`ResizeObserver`),
 * тема из токенов DS (пересборка опций при смене `dark`), `aria`, клики и выделение диапазона,
 * мягкая перезагрузка (прежний кадр на 50 %), экспорт PNG. Страницы используют не её, а готовые компоненты
 * (`TimeSeriesChart`, `RankedBars`, ...), которые строят `option` чистыми функциями `build*Option`.
 */
export const EChart = forwardRef<EChartHandle, EChartProps>(function EChart(
  {
    option,
    height = 320,
    loading = false,
    renderer = 'canvas',
    ariaLabel,
    className,
    texture,
    onMarkClick,
    onMarkHover,
    onAxisClick,
    onBrushX,
    highlight,
    onReady,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const brushRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<EChartsType | null>(null)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const theme = useChartTheme()

  // Последние обработчики — в ref: события привязываются один раз и не пересоздаются на каждый рендер.
  const handlers = useRef({ onMarkClick, onMarkHover, onAxisClick, onBrushX, onReady })
  handlers.current = { onMarkClick, onMarkHover, onAxisClick, onBrushX, onReady }

  useImperativeHandle(
    ref,
    () => ({
      getInstance: () => chartRef.current,
      getDataURL: () =>
        chartRef.current
          ? capturePng(chartRef.current, theme.surface)
          : null,
      exportPng: async (fileName: string, context?: string) => {
        const chart = chartRef.current
        if (!chart) throw new Error('График ещё не готов')
        const url = capturePng(chart, theme.surface)
        const png = context ? await chartPngWithContext(url, context) : url
        // A Blob download is supported by embedded browsers that do not expose
        // data-URL navigation as a downloadable file. Decode only our local PNG.
        const prefix = 'data:image/png;base64,'
        if (!png.startsWith(prefix)) throw new Error('Не удалось подготовить PNG графика')
        const bytes = Uint8Array.from(atob(png.slice(prefix.length)), (char) => char.charCodeAt(0))
        const downloadUrl = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }))
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`
        try {
          document.body.appendChild(link)
          link.click()
        } finally {
          link.remove()
          // Let the browser consume the URL before releasing the backing Blob.
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 0)
        }
      },
    }),
    [theme.surface],
  )

  // Инициализация: движок грузится лениво, при размонтировании экземпляр освобождается.
  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    let disposed = false
    let chart: EChartsType | null = null
    let resizeObserver: ResizeObserver | null = null
    const onWindowResize = () => chart?.resize()

    loadECharts()
      .then((runtime) => {
        if (disposed || !containerRef.current) return
        chart = runtime.init(containerRef.current, null, { renderer, locale: 'RU' })
        chartRef.current = chart

        let clickedSeries: { index: number; id: string } | null = null
        chart.on('click', (params: any) => {
          if (handlers.current.onAxisClick && params.componentType === 'series' && typeof params.dataIndex === 'number' && typeof params.seriesId === 'string') {
            clickedSeries = { index: params.dataIndex, id: params.seriesId }
          }
          handlers.current.onMarkClick?.(params)
        })
        chart.on('mouseover', (params: any) => handlers.current.onMarkHover?.(params))
        chart.on('mouseout', () => handlers.current.onMarkHover?.(null))
        chart.on('globalout', () => handlers.current.onMarkHover?.(null))

        // Клик по категории и выделение диапазона — на уровне zrender: по линии не надо попадать в точку.
        const zr = chart.getZr()
        let dragStart: number | null = null
        let dragged = false
        const showBrush = (from: number, to: number) => {
          const overlay = brushRef.current
          if (!overlay) return
          overlay.style.display = 'block'
          overlay.style.left = `${Math.min(from, to)}px`
          overlay.style.width = `${Math.abs(to - from)}px`
        }
        const hideBrush = () => {
          if (brushRef.current) brushRef.current.style.display = 'none'
        }
        zr.on('mousedown', (event: any) => {
          if (!handlers.current.onBrushX || !chart) return
          if (categoryAt(chart, event.offsetX, event.offsetY) === null) return
          dragStart = event.offsetX
          dragged = false
        })
        zr.on('mousemove', (event: any) => {
          if (dragStart === null) return
          if (Math.abs(event.offsetX - dragStart) >= BRUSH_THRESHOLD) {
            dragged = true
            showBrush(dragStart, event.offsetX)
          }
        })
        zr.on('mouseup', (event: any) => {
          if (dragStart === null || !chart) return
          const start = dragStart
          dragStart = null
          hideBrush()
          if (!dragged) return
          const height = chart.getHeight()
          const y = Math.min(Math.max(event.offsetY, 1), height - 1)
          const a = categoryAt(chart, start, y) ?? categoryAt(chart, start, height / 2)
          const b = categoryAt(chart, event.offsetX, y) ?? categoryAt(chart, Math.max(0, event.offsetX), height / 2)
          if (a !== null && b !== null) handlers.current.onBrushX?.(Math.min(a, b), Math.max(a, b))
        })
        zr.on('globalout', () => {
          dragStart = null
          hideBrush()
        })
        zr.on('click', (event: any) => {
          if (dragged) {
            dragged = false
            clickedSeries = null
            return
          }
          if (!handlers.current.onAxisClick || !chart) { clickedSeries = null; return }
          const index = categoryAt(chart, event.offsetX, event.offsetY)
          // ECharts and zrender dispatch the same click. Resolve its series after both
          // listeners, while preserving clicks in empty plot space and one callback.
          queueMicrotask(() => {
            const mark = clickedSeries
            clickedSeries = null
            if (!disposed && index !== null) handlers.current.onAxisClick?.(index, mark?.index === index ? mark.id : undefined)
          })
        })

        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => chart?.resize())
          resizeObserver.observe(containerRef.current)
        } else {
          window.addEventListener('resize', onWindowResize)
        }
        setReady(true)
        handlers.current.onReady?.(chart)
      })
      .catch(() => {
        if (!disposed) setFailed(true)
      })

    return () => {
      disposed = true
      resizeObserver?.disconnect()
      window.removeEventListener('resize', onWindowResize)
      chart?.dispose()
      chartRef.current = null
    }
    // Рендерер выбирается при создании экземпляра.
  }, [renderer])

  // Данные и тема: опции целиком заменяются (`notMerge`), чтобы убранный ряд не оставался на графике.
  useEffect(() => {
    if (!ready || !chartRef.current) return
    chartRef.current.setOption(withTexture(option, Boolean(texture) || prefersTexture()), { notMerge: true, lazyUpdate: true })
  }, [ready, option, texture])

  // Подсветка извне: наведение на строку таблицы подсвечивает метку графика.
  useEffect(() => {
    const chart = chartRef.current
    if (!ready || !chart) return
    chart.dispatchAction({ type: 'downplay' })
    if (highlight && (highlight.dataIndex !== undefined || highlight.name !== undefined)) {
      chart.dispatchAction({
        type: 'highlight',
        seriesIndex: highlight.seriesIndex ?? 0,
        ...(highlight.dataIndex !== undefined ? { dataIndex: highlight.dataIndex } : { name: highlight.name }),
      })
    }
  }, [ready, highlight])

  const style: CSSProperties = { height: typeof height === 'number' ? `${height}px` : height }

  return (
    <div
      role="figure"
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={cn('relative w-full transition-opacity duration-200', loading && 'opacity-50', className)}
      style={style}
    >
      <div
        ref={containerRef}
        className={cn('h-full w-full', (onAxisClick || onBrushX) && 'cursor-crosshair')}
        data-testid="echart"
      />
      <div
        ref={brushRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 hidden rounded-sm border-x border-indigo-400 bg-indigo-500/10 dark:border-indigo-400 dark:bg-indigo-400/15"
      />
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
          Не удалось загрузить модуль графиков. Обновите страницу.
        </div>
      )}
    </div>
  )
})

export default EChart
